// SD65: the snapshots of a bot recording. A bot recording is audio plus a JPEG of the meeting
// tab every few seconds (Wesley, 2026-10-23), not video, so the player has no picture of its
// own. The watch response lists the snapshot *packs* (`WatchOut.snapshots`); each pack is one
// signed object holding `[u32 BE t_ms][u32 BE len][JPEG]` frames, the same bytes the bot sent
// (server/src/notetaker/snapshots.rs). This module fetches the packs around the playhead and
// says which frame to show: the latest one at or before the audio's current time.
import { useEffect, useMemo, useRef, useState } from "react";
import type { WatchOut } from "../api/types";

export type SnapshotPack = NonNullable<WatchOut["snapshots"]>["packs"][number];

export interface SnapshotFrame {
  /** Offset from the audio's t=0. */
  tMs: number;
  blob: Blob;
}

const HEADER_BYTES = 8;

/** Splits a pack into its frames. Stops at the first frame that doesn't fit, so a truncated
 * download shows what it has rather than nothing. */
export function parsePack(buf: ArrayBuffer): SnapshotFrame[] {
  const view = new DataView(buf);
  const frames: SnapshotFrame[] = [];
  let offset = 0;
  while (offset + HEADER_BYTES <= buf.byteLength) {
    const tMs = view.getUint32(offset, false);
    const len = view.getUint32(offset + 4, false);
    offset += HEADER_BYTES;
    if (len === 0 || offset + len > buf.byteLength) break;
    frames.push({ tMs, blob: new Blob([buf.slice(offset, offset + len)], { type: "image/jpeg" }) });
    offset += len;
  }
  return frames;
}

/** The latest frame whose time is at or before `tMs`, or null before the first one. `frames`
 * is in time order. */
export function frameAt<T extends { tMs: number }>(frames: readonly T[], tMs: number): T | null {
  let lo = 0;
  let hi = frames.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (frames[mid].tMs <= tMs) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return found < 0 ? null : frames[found];
}

/** The index of the pack that holds `tMs` (or the one before it, when the time falls in a gap
 * between packs). `packs` is in time order. Returns -1 before the first pack. */
export function packIndexAt(packs: readonly Pick<SnapshotPack, "first_ms">[], tMs: number): number {
  let found = -1;
  for (let i = 0; i < packs.length; i += 1) {
    if (packs[i].first_ms <= tMs) found = i;
    else break;
  }
  return found;
}

type Entry = { url: string; frames: SnapshotFrame[] | "loading" | "failed" };

export interface ShownSnapshot {
  url: string;
  atMs: number;
}

/** The snapshot to show at `seconds` of playback, as an object URL, or null (no snapshots,
 * nothing fetched yet, or before the first frame). Fetches the pack under the playhead and the
 * next one; a pack whose signed URL has since been replaced (a re-signed watch response) is
 * fetched again, which is also how an expired URL recovers. */
export function useSnapshotFrame(
  snapshots: WatchOut["snapshots"] | null | undefined,
  seconds: number,
  fetchPack: (url: string) => Promise<ArrayBuffer> = defaultFetchPack,
): ShownSnapshot | null {
  const packs = useMemo(() => snapshots?.packs ?? [], [snapshots]);
  const cache = useRef(new Map<number, Entry>());
  const [version, setVersion] = useState(0);
  const fetchRef = useRef(fetchPack);
  fetchRef.current = fetchPack;

  const tMs = Math.max(0, Math.floor(seconds * 1000));
  const index = packIndexAt(packs, tMs);

  useEffect(() => {
    let cancelled = false;
    for (const i of [index, index + 1]) {
      const pack = packs[i];
      if (!pack) continue;
      const have = cache.current.get(pack.seq);
      if (have && have.url === pack.url) continue;
      const entry: Entry = { url: pack.url, frames: "loading" };
      cache.current.set(pack.seq, entry);
      fetchRef
        .current(pack.url)
        .then((buf) => {
          entry.frames = parsePack(buf);
        })
        .catch(() => {
          entry.frames = "failed";
        })
        .finally(() => {
          if (!cancelled) setVersion((v) => v + 1);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [packs, index]);

  const frame = useMemo(() => {
    const pack = packs[index];
    if (!pack) return null;
    const entry = cache.current.get(pack.seq);
    if (!entry || typeof entry.frames === "string") return null;
    return frameAt(entry.frames, tMs);
    // `version` is how a finished fetch reaches this memo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packs, index, tMs, version]);

  const [shown, setShown] = useState<ShownSnapshot | null>(null);
  useEffect(() => {
    if (!frame) {
      setShown(null);
      return;
    }
    if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function") return;
    const url = URL.createObjectURL(frame.blob);
    setShown({ url, atMs: frame.tMs });
    return () => URL.revokeObjectURL(url);
  }, [frame]);
  return shown;
}

async function defaultFetchPack(url: string): Promise<ArrayBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`snapshot pack ${res.status}`);
  return res.arrayBuffer();
}
